/**
 * Set F W-C1 — the draft-lock registry.
 *
 * obs8 CF-4 measured 186 post-finalize draft-PATCH 409s against 117 successes:
 * more conflicts than saves. useAssessmentWizard was already well guarded (Set B
 * W3/W3-b tore down its interval), but a census of the pre-fix source found
 * FIFTEEN draft-write call sites in total, THIRTEEN of them outside that hook,
 * PATCHing /assessments/:id/draft directly and never seeing those guards:
 *
 *   ReportGenerator.tsx:825,868,893   <- the Step 8/9 component, alive AFTER finalize
 *   DdxReviewPanel.tsx:425
 *   SoapNoteEditor.tsx:199
 *   ManagementPlan.tsx:215
 *   MedicalHistoryWizard.tsx:679
 *   InfantAssessment.tsx:290
 *   PaediatricReEvalQuestionnaire.tsx:325
 *   ManagementPlan.tsx:215,228,236
 *   ReexamVisitCount.tsx:57
 *   AIPasteHistory.tsx:464
 *   AssessmentWizard.tsx:1535
 *
 * (I first wrote "nine" from a truncated grep; the full census is fifteen. The
 * number is recorded correctly here because the point of the choke point is that
 * nobody has to keep such a list accurate.)
 *
 * ReportGenerator is the fingerprint obs8 described — irregular 409s clustered
 * after finalize and decaying within ~2 minutes, which is exactly the report and
 * comparison generation calls completing one after another against a locked
 * draft.
 *
 * Guarding nine call sites one at a time would be fragile and the tenth would
 * reintroduce it. This is a single choke point instead: the API layer consults
 * this registry before sending any draft write, so every caller is covered,
 * including ones not written yet.
 */

const lockedAssessments = new Set<string>();

/** Extract the assessment id from a draft URL, or null when it is not one. */
export function assessmentIdFromDraftUrl(url: string): string | null {
  const m = url.match(/\/assessments\/([^/?#]+)\/(?:draft|save-draft)\b/);
  return m ? m[1] : null;
}

/** Record that this assessment is finalized: no further draft write may be sent. */
export function markAssessmentLocked(assessmentId: string): void {
  if (assessmentId) lockedAssessments.add(assessmentId);
}

export function isAssessmentDraftLocked(assessmentId: string | null): boolean {
  return !!assessmentId && lockedAssessments.has(assessmentId);
}

/**
 * Set F W2-B — release the lock when the AUTHOR reopens a record for amendment.
 *
 * Without this, Run C's choke point would keep suppressing every draft write for
 * an assessment that is legitimately editable again, and the amendment surfaces
 * would silently save nothing. Reopen is the one event that makes a locked id
 * writable, so it is the one event allowed to remove it.
 */
export function releaseAssessmentLock(assessmentId: string): void {
  if (assessmentId) lockedAssessments.delete(assessmentId);
}

/** Test seam only. */
export function __resetDraftLocks(): void {
  lockedAssessments.clear();
}

/**
 * The neutral message a locked record shows instead of an error.
 *
 * "Save failed" is WRONG here and was the substance of obs8's complaint: nothing
 * failed. The assessment is finalized and there is deliberately nothing left to
 * save, which is a calm statement of fact, not a fault the clinician must act on.
 */
export const DRAFT_LOCKED_MESSAGE_KEY = 'autosave.finalizedNothingToSave';
export const DRAFT_LOCKED_MESSAGE_EN = 'This assessment is finalized. There is nothing left to save.';
